import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "report-info" }
const _hoisted_2 = { class: "drop-zone__content" }
const _hoisted_3 = {
  key: 0,
  class: "drop-zone__text"
}
const _hoisted_4 = {
  key: 1,
  class: "drop-zone__file"
}
const _hoisted_5 = { class: "drop-zone__filename" }
const _hoisted_6 = { class: "paste-zone" }
const _hoisted_7 = { class: "examples-grid" }

import { ref } from 'vue'
import { useTabsStore } from '@/stores/tabs'

interface ExampleDef {
  type: string
  label: string
  icon: string
}

interface ReportFileData {
  version: string
  exportedAt?: string
  name: string
  state: string
  report: any[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadCard',
  emits: ["upload", "reportLoaded"],
  setup(__props, { emit: __emit }) {

const store = useTabsStore()

const emit = __emit

// ─── State ────────────────────────────────────────────────────────────────────

const activeTab = ref('file')
const data = ref<string | null>(null)
const file = ref<File | null>(null)
const pastedContent = ref('')
const dragOver = ref(false)
const dialog = ref(false)
const reportDialog = ref(false)
const reportData = ref<ReportFileData | null>(null)
const reportInfo = ref({ name: '', exportedAt: '', resultCount: 0 })
const fileInputRef = ref<HTMLInputElement | null>(null)

const examples: ExampleDef[] = [
  { type: 'roa', label: 'ROA', icon: 'mdi-shield-check-outline' },
  { type: 'mft', label: 'Manifest', icon: 'mdi-format-list-checks' },
  { type: 'crl', label: 'CRL', icon: 'mdi-close-circle-outline' },
  { type: 'cer', label: 'Certificate', icon: 'mdi-certificate-outline' },
  { type: 'asa', label: 'ASPA', icon: 'mdi-link-variant' },
  { type: 'gbr', label: 'Ghostbuster', icon: 'mdi-ghost-outline' },
  { type: 'tls', label: 'TLS', icon: 'mdi-lock-outline' }
]

// ─── Methods ──────────────────────────────────────────────────────────────────

function loadExample(type: string): void {
  store.addTab(type + '_example')
  store.stateSet({
    tab: store.currentTab,
    type: 'example',
    data: type
  })
  emit('upload')
}

function open(newTab: boolean): void {
  dialog.value = false
  const type = file.value?.name.endsWith('.json') ? 'json' : 'hex'

  if (newTab) {
    store.addTab(file.value?.name ?? 'Unnamed')
  }

  store.stateSet({
    tab: store.currentTab,
    data: data.value,
    type: type as any
  })

  clearFile()
  emit('upload')
}

function isReportFile(jsonData: string): boolean {
  try {
    const parsed = JSON.parse(jsonData)
    return !!(parsed.version && parsed.state && parsed.report && Array.isArray(parsed.report))
  } catch {
    return false
  }
}

function loadReportObject(newTab: boolean): void {
  reportDialog.value = false
  if (!reportData.value) return

  const tabName = reportData.value.name || 'Loaded from Report'

  if (newTab) {
    store.addTab(tabName)
  }

  store.stateSet({
    tab: store.currentTab,
    data: reportData.value.state,
    type: 'json'
  })

  emit('reportLoaded', {
    name: reportData.value.name,
    state: reportData.value.state,
    report: reportData.value.report
  })

  reportData.value = null
  clearFile()
  emit('upload')
}

async function processFile(f: File): Promise<void> {
  file.value = f

  try {
    if (f.name.endsWith('.json')) {
      const jsonText = await f.text()

      if (isReportFile(jsonText)) {
        const parsed = JSON.parse(jsonText) as ReportFileData
        reportData.value = parsed
        reportInfo.value = {
          name: parsed.name ?? 'Unknown',
          exportedAt: parsed.exportedAt ? new Date(parsed.exportedAt).toLocaleString() : 'Unknown',
          resultCount: parsed.report ? parsed.report.length : 0
        }
        reportDialog.value = true
        return
      }

      data.value = jsonText
    } else {
      try {
        const decoder = new TextDecoder('utf-8', { fatal: true })
        const arrayBuffer = await f.arrayBuffer()
        data.value = decoder.decode(arrayBuffer)
      } catch {
        const arrayBuffer = await f.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)
        data.value = [...uint8Array].map(byte => byte.toString(16).padStart(2, '0').toUpperCase()).join('')
      }
    }
  } catch (err) {
    console.error('Error processing file:', err)
    alert(`Error processing ${f.name}. Please ensure the file is valid.`)
    file.value = null
  }
}

function triggerFileInput(): void {
  fileInputRef.value?.click()
}

function clearFile(): void {
  file.value = null
  data.value = null
  reportData.value = null
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

async function handleFileSelect(event: Event): Promise<void> {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (!files) return
  const multiple = files.length > 1

  for (const f of files) {
    await processFile(f)
    if (reportDialog.value) continue
    if (multiple || store.tabs.length === 0) {
      open(true)
    } else {
      dialog.value = true
    }
  }
}

async function handleDrop(event: DragEvent): Promise<void> {
  dragOver.value = false
  const files = event.dataTransfer?.files
  if (!files) return
  const multiple = files.length > 1

  for (const f of files) {
    await processFile(f)
    if (reportDialog.value) continue
    if (multiple || store.tabs.length === 0) {
      open(true)
    } else {
      dialog.value = true
    }
  }
}

function handlePastedContent(): void {
  if (!pastedContent.value) return
  let content = pastedContent.value.trim()

  if (/^[0-9A-Fa-f\s]+$/.test(content)) { //if hex with space
  content = content.replace(/\s+/g, '')   //remove space/ newline
  }

  data.value = content
  file.value = { name: 'Pasted Content' } as File

  if (store.tabs.length === 0) {
    open(true)
  } else {
    dialog.value = true
  }

  pastedContent.value = ''
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "upload-container"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_dialog, {
        modelValue: dialog.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialog).value = $event)),
        "max-width": "400"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "confirm-dialog" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                  _createTextVNode("Open in new tab?", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "text-body-1" }, {
                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                  _createTextVNode(" Do you want to open the file in a new tab? ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "justify-end pa-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "tonal",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (dialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "tonal",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (open(false)))
                  }, {
                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                      _createTextVNode("This tab", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    color: "primary",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (open(true)))
                  }, {
                    default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                      _createTextVNode("New tab", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: reportDialog.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((reportDialog).value = $event)),
        "max-width": "500"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "report-dialog" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                      _createTextVNode("mdi-file-document-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[18] || (_cache[18] = _createTextVNode(" Report File Detected ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "text-body-1" }, {
                default: _withCtx(() => [
                  _cache[22] || (_cache[22] = _createElementVNode("p", null, "This file contains a saved report with an ASN.1 object.", -1)),
                  _createVNode(_component_v_divider, { class: "my-3" }),
                  _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", null, [
                      _cache[19] || (_cache[19] = _createElementVNode("strong", null, "Name:", -1)),
                      _createTextVNode(" " + _toDisplayString(reportInfo.value.name), 1)
                    ]),
                    _createElementVNode("div", null, [
                      _cache[20] || (_cache[20] = _createElementVNode("strong", null, "Exported:", -1)),
                      _createTextVNode(" " + _toDisplayString(reportInfo.value.exportedAt), 1)
                    ]),
                    _createElementVNode("div", null, [
                      _cache[21] || (_cache[21] = _createElementVNode("strong", null, "Test Results:", -1)),
                      _createTextVNode(" " + _toDisplayString(reportInfo.value.resultCount) + " RP(s) tested", 1)
                    ])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "justify-end pa-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "tonal",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (reportDialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    color: "primary",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (loadReportObject(true)))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { start: "" }, {
                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                          _createTextVNode("mdi-plus", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[25] || (_cache[25] = _createTextVNode(" Load in New Tab ", -1))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_card, {
        class: "upload-card",
        elevation: "0"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_tabs, {
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((activeTab).value = $event)),
            color: "primary",
            class: "upload-tabs"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_tab, {
                value: "file",
                class: "tab-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                      _createTextVNode("mdi-file-upload-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[27] || (_cache[27] = _createTextVNode(" File ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_tab, {
                value: "paste",
                class: "tab-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                      _createTextVNode("mdi-content-paste", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[29] || (_cache[29] = _createTextVNode(" Paste ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_tab, {
                value: "example",
                class: "tab-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                      _createTextVNode("mdi-file-document-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[31] || (_cache[31] = _createTextVNode(" Examples ", -1))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_component_v_divider),
          _createVNode(_component_v_window, {
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((activeTab).value = $event)),
            class: "tab-content"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_window_item, { value: "file" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: _normalizeClass(["drop-zone", { 'drop-zone--active': dragOver.value, 'drop-zone--has-file': file.value }]),
                    onDragover: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (dragOver.value = true), ["prevent"])),
                    onDragleave: _cache[9] || (_cache[9] = _withModifiers(($event: any) => (dragOver.value = false), ["prevent"])),
                    onDrop: _withModifiers(handleDrop, ["prevent"]),
                    onClick: triggerFileInput
                  }, [
                    _createElementVNode("input", {
                      type: "file",
                      ref_key: "fileInputRef",
                      ref: fileInputRef,
                      style: {"display":"none"},
                      onChange: handleFileSelect,
                      multiple: ""
                    }, null, 544),
                    _createElementVNode("div", _hoisted_2, [
                      _createVNode(_component_v_icon, {
                        icon: file.value ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline',
                        color: file.value ? 'success' : 'primary',
                        size: "48",
                        class: "drop-zone__icon"
                      }, null, 8, ["icon", "color"]),
                      (!file.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[32] || (_cache[32] = [
                            _createElementVNode("span", { class: "drop-zone__title" }, "Drop files here", -1),
                            _createElementVNode("span", { class: "drop-zone__subtitle" }, "or click to browse", -1)
                          ]))]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("span", _hoisted_5, _toDisplayString(file.value.name), 1),
                            _createVNode(_component_v_btn, {
                              icon: "mdi-close",
                              size: "x-small",
                              variant: "text",
                              onClick: _withModifiers(clearFile, ["stop"]),
                              class: "drop-zone__clear"
                            })
                          ]))
                    ]),
                    _cache[33] || (_cache[33] = _createElementVNode("div", { class: "drop-zone__hint" }, " Supports DER, PEM, Base64, JSON, and Report files ", -1))
                  ], 34)
                ]),
                _: 1
              }),
              _createVNode(_component_v_window_item, { value: "paste" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_v_textarea, {
                      modelValue: pastedContent.value,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((pastedContent).value = $event)),
                      variant: "outlined",
                      label: "Paste Base64, Hex, or PEM content",
                      placeholder: "Paste your encoded content here...",
                      rows: "6",
                      "auto-grow": "",
                      "hide-details": "",
                      class: "paste-textarea"
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      variant: "flat",
                      disabled: !pastedContent.value,
                      onClick: handlePastedContent,
                      class: "mt-4",
                      block: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { start: "" }, {
                          default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                            _createTextVNode("mdi-check", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[35] || (_cache[35] = _createTextVNode(" Load Content ", -1))
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_window_item, { value: "example" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_7, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(examples, (example) => {
                      return _createVNode(_component_v_btn, {
                        key: example.type,
                        variant: "tonal",
                        color: "primary",
                        onClick: ($event: any) => (loadExample(example.type)),
                        class: "example-btn"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, { start: "" }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(example.icon), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createTextVNode(" " + _toDisplayString(example.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick"])
                    }), 64))
                  ]),
                  _cache[36] || (_cache[36] = _createElementVNode("p", { class: "examples-hint" }, "Load a sample RPKI object to explore the editor", -1))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})